import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@babackapp/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import useGetPayementIntent from "@babackapp/private-api-client/client/use-get-stripe-payment-intent-details";
import useFormatPrice from "@/hooks/use-format-price.ts";
import { Loader2 } from "lucide-react";
import useStripeIntegrationPaymentRefundMutation from "@babackapp/private-api-client/client/use-stripe-integration-payment-refund";
import { currencyFromISO } from "@/utils/currency.ts";
import { useCurrentReturnRequest } from "@/pages/(app)/returns/utils/current-return-request-context.tsx";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import useTranslation, {
  Translation,
} from "@babackapp/translations/use-translation";
import { ApiError } from "@babackapp/private-api-client/generated/requests/core/ApiError";

// Define the form schema with Zod
const createFormSchema = (
  t: (key: Translation, options?: Record<string, unknown>) => string,
  maxRefundable?: number,
) =>
  z.object({
    amount: z.coerce
      .number()
      // .positive("Amount must be positive")
      .positive(t("returnDetail.refundModal.amountMustBePositive"))
      // .min(0.01, "Amount must be at least 0.01")
      .min(0.01, t("returnDetail.refundModal.amountMinimum"))
      .refine(
        (val) => !maxRefundable || val <= maxRefundable,
        () => ({
          message: t(
            "returnDetail.refundModal.amountCannotExceedMaxRefundable",
          ),
        }),
      ),
  });

// Define the form values type
type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

interface StripeRefundFormProps {
  intentId: string;
  onSubmit: () => void;
}

export default function StripeRefundForm({
  intentId,
  onSubmit,
}: StripeRefundFormProps) {
  const t = useTranslation();
  const formatPrice = useFormatPrice();
  const { data: shop } = useCurrentShopSuspense();
  const returnRequest = useCurrentReturnRequest();

  const {
    data: paymentDetails,
    isLoading,
    error: queryError,
  } = useGetPayementIntent(shop.id, intentId);
  const {
    isPending,
    mutateAsync: createRefund,
    error: mutationError,
    failureReason,
  } = useStripeIntegrationPaymentRefundMutation(shop);

  const formSchema = createFormSchema(t, paymentDetails?.maxRefundable);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: 0,
    },
    values: paymentDetails
      ? {
          amount: paymentDetails.maxRefundable,
        }
      : undefined,
    // This ensures validation re-runs when the schema changes
    context: {
      maxRefundable: paymentDetails?.maxRefundable,
    },
  });

  // Handle form submission
  const handleSubmit = async (values: FormValues) => {
    if (!returnRequest.financial_informations?.intentId) {
      console.error("Payment intent ID is missing");
      return;
    }
    try {
      await createRefund({
        paymentIntentId: intentId,
        amount: values.amount,
        returnRequestId: returnRequest.id,
      });
      await onSubmit();
    } catch (error) {
      console.error("Error submitting refund:", error);
    } finally {
    }
  };

  if (queryError) {
    return (
      <div className="text-red-500 text-sm">
        {queryError instanceof Error
          ? queryError.message
          : t("returnDetail.refundModal.somethingWrong")}
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      {isLoading ? (
        <div className="flex justify-center gap-2">
          <span className="text-xs font-semibold">{t("misc.loading")}</span>
          <Loader2 className="size-3 animate-spin" />
        </div>
      ) : (
        <>
          {paymentDetails && (
            <div className="mb-4 p-3 bg-muted rounded-md">
              <p className="text-sm font-medium">
                {t("returnDetail.refundModal.maxRefundableAmount")}:{" "}
                {formatPrice({
                  amount: paymentDetails.maxRefundable,
                  currency: paymentDetails.currency,
                })}
              </p>
            </div>
          )}

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>
                      {t("returnDetail.refundModal.amount")}
                    </FormLabel>
                    <FormControl>
                      <div className="flex items-center">
                        <Input
                          type="number"
                          step="0.01"
                          // placeholder="Enter amount"
                          placeholder={t(
                            "returnDetail.refundModal.enterAmount",
                          )}
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(
                              value === "" ? 0 : parseFloat(value),
                            );
                          }}
                          className="w-[200px]"
                        />
                        <span className="ml-2 text-lg font-bold">
                          {currencyFromISO(
                            paymentDetails?.currency.toUpperCase() || "EUR",
                          )}
                        </span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {mutationError && (
                <div className="text-red-500 text-sm">
                  {((failureReason as ApiError)?.body as { message?: string })
                    ?.message ||
                    (mutationError as Error)?.message ||
                    t("returnDetail.refundModal.somethingWrong")}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || isPending}
              >
                {isPending
                  ? t("returnDetail.refundModal.processing")
                  : t("returnDetail.refundModal.process")}
              </Button>
            </form>
          </Form>
        </>
      )}
    </div>
  );
}
